"use client";

import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { ComposerPrimitive } from "@assistant-ui/react";
import {
  BriefcaseIcon,
  CheckIcon,
  FileTextIcon,
  GlobeIcon,
  HardDriveIcon,
  PlusIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { McpServerIcon } from "@/lib/mcpServerIcons";
import { useMcpServers } from "@/components/custom/contexts/mcpServersContext";
import { useWebSearch } from "@/components/custom/contexts/webSearchContext";
import { useAiProvider } from "@/components/custom/contexts/aiProviderContext";
import { useComposerDocActions } from "./ComposerDocumentsStrip";
import { PraetorComposedIcon } from "@/components/custom/icons/PraetorComposedIcon";

export const ComposerAddMenu: FC = () => {
  const { t } = useTranslation();
  const { handleAddDocuments, handleAddCase } = useComposerDocActions();
  const {
    allServers,
    selectedServers,
    serverHealth,
    toggleServer,
    openSourceUnavailable,
  } = useMcpServers();
  const { webSearch, toggleWebSearch } = useWebSearch();
  const { allProviders, selectedProvider } = useAiProvider();

  const isWebSearchAvailable = useMemo(() => {
    const active = allProviders.find(p =>
      selectedProvider ? p.name === selectedProvider : p.isDefault,
    );
    return active?.capabilities.webSearch === true;
  }, [allProviders, selectedProvider]);

  const toggleableServers = allServers.filter(s => !s.required);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 rounded-lg p-1 font-semibold text-xs hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30"
          aria-label={t("composer.addSource")}
          aria-haspopup="menu"
        >
          <PlusIcon className="size-5 stroke-[1.5px]" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent side="top" align="start" sideOffset={4} className="min-w-48 max-w-80 rounded-xl p-1.5">
        {/* ── PŘIPOJIT K DOTAZU ── */}
        <div className="px-2 pt-1.5 pb-1">
          <span className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
            {t("composer.menu.attachSection")}
          </span>
        </div>

        <DropdownMenuItem onSelect={handleAddCase} className="cursor-pointer min-h-8 rounded-lg">
          <PraetorComposedIcon
            overlay={<BriefcaseIcon className="size-[8px]" strokeWidth={2.5} />}
            alt={t("composer.menu.addCase")}
          />
          <span className="flex-1">{t("composer.menu.addCase")}</span>
        </DropdownMenuItem>

        {/*
          The Radix `onSelect` is called with a SyntheticEvent. If we passed
          `handleAddDocuments` directly, the event would land as the `caseHint`
          arg and bypass the route-context fallback inside the hook, forwarding
          an Event object into the Praetor SDK. Wrap in a lambda to discard the
          arg and let the hook resolve the default caseId itself.
        */}
        <DropdownMenuItem onSelect={() => handleAddDocuments()} className="cursor-pointer min-h-8 rounded-lg">
          <PraetorComposedIcon
            overlay={<FileTextIcon className="size-[8px]" strokeWidth={2.5} />}
            alt={t("composer.menu.addPraetorDocuments")}
          />
          <span className="flex-1">{t("composer.menu.addPraetorDocuments")}</span>
        </DropdownMenuItem>

        <ComposerPrimitive.AddAttachment asChild>
          <DropdownMenuItem className="cursor-pointer min-h-8 rounded-lg">
            <HardDriveIcon className="size-4 shrink-0" />
            <span>{t("composer.menu.addLocalAttachment")}</span>
          </DropdownMenuItem>
        </ComposerPrimitive.AddAttachment>

        <DropdownMenuSeparator className="mx-2 my-1.5" />

        {/* ── HLEDAT V ── */}
        <div className="flex items-center justify-between px-2 pt-1.5 pb-1">
          <span className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
            {t("composer.menu.searchSection")}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {t("composer.menu.selectSource")}
          </span>
        </div>

        {/* MCP server toggles — rendered before Web so Web is always last. */}
        {toggleableServers.map(server => {
          const isSelected = selectedServers.includes(server.name);
          const isPaywalled = serverHealth[server.name]?.status === "paywalled";
          return (
            <DropdownMenuItem
              key={server.name}
              className="cursor-pointer min-h-8 rounded-lg"
              onSelect={async (e) => {
                e.preventDefault();
                // If we already know the server is paywalled (auto-validated at
                // startup or marked from a prior click), short-circuit to the
                // modal without bouncing through validate again.
                if (isPaywalled) {
                  openSourceUnavailable(server.name, "paywalled");
                  return;
                }
                const result = await toggleServer(server.name);
                if (result?.status === "paywalled") {
                  openSourceUnavailable(server.name, "paywalled");
                } else if (result?.status === "error") {
                  openSourceUnavailable(server.name, "error", result.error);
                }
              }}
            >
              <McpServerIcon name={server.name} />
              <span className="flex-1">{t(`${server.name}.label`)}</span>
              {isSelected && !isPaywalled && (
                <CheckIcon className="size-4 shrink-0 text-foreground" />
              )}
            </DropdownMenuItem>
          );
        })}

        {/* Web search toggle — always last. */}
        <DropdownMenuItem
          className="cursor-pointer min-h-8 rounded-lg"
          onSelect={(e) => {
            e.preventDefault();
            if (isWebSearchAvailable) toggleWebSearch();
          }}
          disabled={!isWebSearchAvailable}
        >
          <GlobeIcon className="size-4 shrink-0" />
          <span className="flex-1">{t("composer.web")}</span>
          {webSearch && <CheckIcon className="size-4 shrink-0 text-foreground" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
